import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, XCircle, AlertTriangle, Server, Database, Code2, 
  Layers, Activity, Zap, RefreshCw, Box, Cloud, FileCode, 
  Network, Settings, BarChart3, Search
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';

const API = `${BACKEND_URL}/api`;

export default function DiscoveryEngine() {
  const [landscape, setLandscape] = useState(null);
  const [discoveries, setDiscoveries] = useState([]);
  const [platformDiscovery, setPlatformDiscovery] = useState(null);
  const [sapLandscape, setSapLandscape] = useState(null);
  const [configuredSystems, setConfiguredSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [sapLoading, setSapLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchConfiguredSystems();
  }, []);

  const fetchConfiguredSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-systems`);
      setConfiguredSystems(response.data);
      // Auto-select first system if available
      if (response.data.length > 0 && !selectedSystem) {
        setSelectedSystem(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching configured systems:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [landscapeRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/discovery/landscape/overview`),
        axios.get(`${API}/tickets`)
      ]);

      setLandscape(landscapeRes.data);

      // Fetch discoveries for all tickets
      const discoveryPromises = ticketsRes.data.map(async (ticket) => {
        try {
          const discoveryRes = await axios.get(`${API}/discovery/${ticket.ticket_number}`);
          return { ...discoveryRes.data, ticket };
        } catch (err) {
          return null;
        }
      });

      const discoveryResults = await Promise.all(discoveryPromises);
      setDiscoveries(discoveryResults.filter(d => d !== null));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runPlatformDiscovery = async () => {
    setDiscoveryLoading(true);
    try {
      const response = await axios.get(`${API}/discovery/system/platform`);
      setPlatformDiscovery(response.data);
    } catch (error) {
      console.error('Error running platform discovery:', error);
      alert('Error running platform discovery: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const runSAPLandscapeDiscovery = async () => {
    if (!selectedSystem) {
      alert('Please select an SAP system first, or configure one in Settings');
      return;
    }
    
    setSapLoading(true);
    try {
      const response = await axios.get(`${API}/discovery/landscape/comprehensive?system_id=${selectedSystem}`);
      setSapLandscape(response.data);
    } catch (error) {
      console.error('Error running SAP landscape discovery:', error);
      alert('Error running SAP landscape discovery: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSapLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  const totalCustomizations = discoveries.reduce((sum, d) => sum + d.total_customizations, 0);
  const totalCompliant = discoveries.reduce((sum, d) => sum + d.clean_core_compliant, 0);
  const totalNonCompliant = discoveries.reduce((sum, d) => sum + d.non_compliant, 0);
  const complianceRate = totalCustomizations > 0 ? ((totalCompliant / totalCustomizations) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="discovery-engine">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Discovery Engine</h1>
          <p className="text-muted-foreground mt-2">Comprehensive platform and landscape analysis</p>
        </div>
        <div className="flex items-center gap-3">
          {configuredSystems.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">SAP System:</label>
              <Select value={selectedSystem || ''} onValueChange={setSelectedSystem}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select SAP system" />
                </SelectTrigger>
                <SelectContent>
                  {configuredSystems.map((system) => (
                    <SelectItem key={system.id} value={system.id}>
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        {system.name} ({system.environment})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button 
            onClick={runPlatformDiscovery} 
            disabled={discoveryLoading}
            variant="outline"
            className="gap-2"
          >
            {discoveryLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Activity className="h-4 w-4" />
            )}
            Discover Platform
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Scans</CardTitle>
            <Server className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{discoveries.length}</div>
            <p className="text-xs text-muted-foreground">Discovery scans completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliant</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalCompliant}</div>
            <p className="text-xs text-muted-foreground">Clean Core compliant</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Non-Compliant</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{totalNonCompliant}</div>
            <p className="text-xs text-muted-foreground">Requires attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{complianceRate}%</div>
            <p className="text-xs text-muted-foreground">Overall compliance</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="sap-landscape" className="w-full">
        <TabsList>
          <TabsTrigger value="sap-landscape">
            <Cloud className="h-4 w-4 mr-2" />
            SAP Landscape
          </TabsTrigger>
          <TabsTrigger value="platform">
            <Box className="h-4 w-4 mr-2" />
            Halo Platform
          </TabsTrigger>
          <TabsTrigger value="discoveries">
            <BarChart3 className="h-4 w-4 mr-2" />
            Discovery Results
          </TabsTrigger>
        </TabsList>

        {/* SAP Landscape Tab - NEW DEFAULT */}
        <TabsContent value="sap-landscape">
          {!sapLandscape ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Cloud className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">SAP Landscape Discovery</h3>
                <p className="text-muted-foreground mb-4">
                  Comprehensive analysis of selected SAP system, modules, customizations, and integration landscape
                </p>
                
                {configuredSystems.length === 0 ? (
                  <div className="space-y-3">
                    <Alert className="max-w-md mx-auto">
                      <AlertDescription>
                        No SAP systems configured. Go to Settings → SAP Landscape to add your SAP systems.
                      </AlertDescription>
                    </Alert>
                    <Button variant="outline" onClick={() => window.location.href = '/settings'}>
                      <Settings className="h-4 w-4 mr-2" />
                      Go to Settings
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedSystem && (
                      <div className="max-w-md mx-auto p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm font-medium mb-2">Selected System:</div>
                        <div className="flex items-center justify-center gap-2">
                          <Server className="h-5 w-5 text-blue-600" />
                          <span className="font-semibold text-lg">
                            {configuredSystems.find(s => s.id === selectedSystem)?.name}
                          </span>
                          <Badge>
                            {configuredSystems.find(s => s.id === selectedSystem)?.environment}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          {configuredSystems.find(s => s.id === selectedSystem)?.type}
                        </div>
                      </div>
                    )}
                    
                    <Button onClick={runSAPLandscapeDiscovery} disabled={sapLoading || !selectedSystem} size="lg">
                      {sapLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Discovering SAP Landscape...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Discover SAP Landscape
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-4">
                {/* Discovery Context */}
                {sapLandscape.discovery_context && (
                  <Card className="border-2 border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <Server className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-lg">{sapLandscape.discovery_context.system_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {sapLandscape.discovery_context.system_type} • Client {sapLandscape.discovery_context.client}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="mb-1">{sapLandscape.discovery_context.environment}</Badge>
                          <div className="text-xs text-muted-foreground">
                            Mode: {sapLandscape.discovery_context.discovery_mode}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-blue-800 bg-blue-100 p-2 rounded">
                        🔍 Discovery performed on: {sapLandscape.discovery_context.url}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Landscape Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Cloud className="h-5 w-5 text-blue-600" />
                      SAP Landscape Overview
                    </CardTitle>
                    <CardDescription>Connected SAP systems and overall health</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                          {sapLandscape.landscape_overview.total_systems}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">SAP Systems</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">
                          {sapLandscape.landscape_overview.total_modules}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Total Modules</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-3xl font-bold text-orange-600">
                          {sapLandscape.landscape_overview.total_customizations}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Customizations</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-3xl font-bold text-green-600">
                          {sapLandscape.landscape_overview.clean_core_compliance}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Clean Core</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <Badge className="text-lg px-4 py-2">
                          {sapLandscape.landscape_overview.landscape_health}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">Health Status</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* SAP Systems Detail */}
                {sapLandscape.systems.map((system, idx) => (
                  <Card key={system.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Server className="h-5 w-5" />
                            {system.name}
                          </CardTitle>
                          <CardDescription>
                            {system.type} • {system.version} • {system.environment}
                          </CardDescription>
                        </div>
                        <Badge variant={system.status.includes('Running') ? 'default' : 'secondary'}>
                          {system.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* System Info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                          <div>
                            <div className="text-xs text-muted-foreground">Location</div>
                            <div className="font-medium">{system.location}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Database</div>
                            <div className="font-medium">{system.database.type}</div>
                            <div className="text-xs">{system.database.size}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Customizations</div>
                            <div className="font-medium">{system.customization_count}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Availability</div>
                            <div className="font-medium">{system.performance_metrics.availability}</div>
                          </div>
                        </div>

                        {/* Installed Modules */}
                        <div>
                          <div className="font-semibold mb-2 flex items-center gap-2">
                            <Layers className="h-4 w-4" />
                            Installed Modules ({system.installed_modules.length})
                          </div>
                          <div className="space-y-2">
                            {system.installed_modules.map((module, midx) => (
                              <div key={midx} className="p-3 border rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{module.code}</Badge>
                                    <span className="font-medium">{module.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary">{module.usage} usage</Badge>
                                    <Badge variant={module.status === 'active' ? 'default' : 'outline'}>
                                      {module.status}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <span className="text-muted-foreground">Total Customizations: </span>
                                    <span className="font-semibold">{module.customizations}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Clean Core: </span>
                                    <span className="font-semibold text-green-600">{module.clean_core_compliant}</span>
                                    <span className="text-muted-foreground"> / {module.customizations}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Custom Code Objects */}
                        <div>
                          <div className="font-semibold mb-2">Custom Code Objects</div>
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                            {Object.entries(system.custom_code_objects).map(([key, value]) => (
                              <div key={key} className="p-2 border rounded text-center">
                                <div className="text-lg font-bold">{value}</div>
                                <div className="text-xs text-muted-foreground">
                                  {key.replace(/_/g, ' ').toUpperCase()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Performance Metrics */}
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-semibold mb-2 text-sm">Performance Metrics</div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-muted-foreground text-xs">Avg Response Time</div>
                              <div className="font-semibold">{system.performance_metrics.avg_response_time}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs">Peak Users</div>
                              <div className="font-semibold">{system.performance_metrics.peak_users}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs">Availability</div>
                              <div className="font-semibold">{system.performance_metrics.availability}</div>
                            </div>
                          </div>
                        </div>

                        {/* Migration Status (if applicable) */}
                        {system.migration_status && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-semibold text-sm">Migration to {system.migration_status.target_system}</div>
                              <Badge variant="outline">Target: {system.migration_status.target_date}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Progress value={parseFloat(system.migration_status.readiness)} className="flex-1" />
                              <span className="text-sm font-semibold">{system.migration_status.readiness} Ready</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Landscape Topology */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Network className="h-5 w-5 text-indigo-600" />
                      Landscape Topology & Connections
                    </CardTitle>
                    <CardDescription>System connections and data flows</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Regions */}
                      <div>
                        <div className="font-semibold mb-2">Regions</div>
                        <div className="flex gap-2">
                          {sapLandscape.landscape_topology.regions.map((region, idx) => (
                            <div key={idx} className="p-3 border rounded-lg flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-medium">{region.name}</div>
                                {region.primary && <Badge>Primary</Badge>}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {region.systems.length} system(s)
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Connections */}
                      <div>
                        <div className="font-semibold mb-2">System Connections</div>
                        <div className="space-y-2">
                          {sapLandscape.landscape_topology.connections.map((conn, idx) => (
                            <div key={idx} className="p-3 border rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline">{conn.from}</Badge>
                                  <span>→</span>
                                  <Badge variant="outline">{conn.to}</Badge>
                                </div>
                                <Badge variant={conn.status === 'Active' ? 'default' : 'secondary'}>
                                  {conn.status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-4 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Type: </span>
                                  <span className="font-medium">{conn.type}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Protocol: </span>
                                  <span className="font-medium">{conn.protocol}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Frequency: </span>
                                  <span className="font-medium">{conn.frequency}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Volume: </span>
                                  <span className="font-medium">{conn.data_volume}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* External Connections */}
                      <div>
                        <div className="font-semibold mb-2">External Integrations</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {sapLandscape.landscape_topology.external_connections.map((conn, idx) => (
                            <div key={idx} className="p-2 border rounded-lg text-sm">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{conn.external_service}</div>
                                  <div className="text-xs text-muted-foreground">{conn.type}</div>
                                </div>
                                <Badge variant="outline" className="text-xs">{conn.protocol}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Integration Landscape */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-600" />
                      Integration Landscape
                    </CardTitle>
                    <CardDescription>
                      Integration technologies and API catalog ({sapLandscape.integration_landscape.total_integrations} integrations)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Integration Patterns */}
                      <div>
                        <div className="font-semibold mb-2">Integration Patterns</div>
                        <div className="grid grid-cols-4 gap-2">
                          {Object.entries(sapLandscape.integration_landscape.integration_patterns).map(([pattern, count]) => (
                            <div key={pattern} className="p-3 border rounded-lg text-center">
                              <div className="text-2xl font-bold text-indigo-600">{count}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {pattern.replace(/_/g, ' ').toUpperCase()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Integration Technologies */}
                      <div>
                        <div className="font-semibold mb-2">Integration Technologies</div>
                        <div className="space-y-2">
                          {sapLandscape.integration_landscape.integration_technologies.map((tech, idx) => (
                            <div key={idx} className="p-3 border rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-medium">{tech.name}</div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">{tech.usage_count} uses</Badge>
                                  <Badge variant={tech.status.includes('Active') ? 'default' : 'secondary'}>
                                    {tech.status}
                                  </Badge>
                                </div>
                              </div>
                              {tech.recommendation && (
                                <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                                  💡 {tech.recommendation}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* API Catalog */}
                      <div>
                        <div className="font-semibold mb-2">
                          API Catalog ({sapLandscape.integration_landscape.api_catalog.total_apis} APIs)
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {sapLandscape.integration_landscape.api_catalog.categories.map((cat, idx) => (
                            <div key={idx} className="p-2 border rounded-lg text-center">
                              <div className="text-lg font-bold">{cat.count}</div>
                              <div className="text-xs text-muted-foreground">{cat.name} APIs</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Technical Debt Analysis */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      Technical Debt Analysis
                    </CardTitle>
                    <CardDescription>
                      Overall Score: {sapLandscape.technical_debt_analysis.overall_debt_score} • 
                      Estimated Effort: {sapLandscape.technical_debt_analysis.estimated_effort_to_resolve}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {sapLandscape.technical_debt_analysis.categories.map((cat, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">{cat.category}</div>
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                cat.severity === 'Critical' ? 'destructive' :
                                cat.severity === 'High' ? 'destructive' :
                                cat.severity === 'Medium' ? 'default' : 'secondary'
                              }>
                                {cat.severity}
                              </Badge>
                              <Badge variant="outline">{cat.count} items</Badge>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">{cat.description}</div>
                          <div className="flex items-center justify-between text-xs">
                            <div className="text-blue-600 font-medium">💡 {cat.recommendation}</div>
                            <div className="text-muted-foreground">{cat.effort_hours}h effort</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Modernization Roadmap */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-green-600" />
                      Modernization Roadmap
                    </CardTitle>
                    <CardDescription>
                      Investment: {sapLandscape.modernization_roadmap.investment_summary.total_budget} • 
                      ROI: {sapLandscape.modernization_roadmap.investment_summary.roi_projected}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Phases */}
                      {sapLandscape.modernization_roadmap.phases.map((phase, idx) => (
                        <div key={idx} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold">{phase.phase}</div>
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                phase.status === 'Completed' ? 'default' :
                                phase.status === 'In Progress' ? 'default' : 'outline'
                              }>
                                {phase.status}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{phase.duration}</span>
                            </div>
                          </div>
                          <Progress value={phase.completion} className="mb-2" />
                          <div className="text-sm text-muted-foreground">
                            {phase.activities.map((activity, aidx) => (
                              <div key={aidx} className="text-xs">• {activity}</div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Key Milestones */}
                      <div>
                        <div className="font-semibold mb-2">Key Milestones</div>
                        <div className="space-y-1">
                          {sapLandscape.modernization_roadmap.key_milestones.map((milestone, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 border rounded text-sm">
                              <div className="flex items-center gap-2">
                                {milestone.status === 'Completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                                {milestone.status === 'On Track' && <Activity className="h-4 w-4 text-blue-600" />}
                                {milestone.status === 'Planning' && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                                <span>{milestone.milestone}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{milestone.target_date}</span>
                                <Badge variant="outline" className="text-xs">{milestone.status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Halo Platform Architecture Tab */}
        <TabsContent value="platform">
          {!platformDiscovery ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Activity className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Platform Discovery</h3>
                <p className="text-muted-foreground mb-4">
                  Analyze the Halo platform architecture, modules, database, and integrations
                </p>
                <Button onClick={runPlatformDiscovery} disabled={discoveryLoading}>
                  {discoveryLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Discovering...
                    </>
                  ) : (
                    <>
                      <Activity className="h-4 w-4 mr-2" />
                      Run Platform Discovery
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-4">
                {/* Executive Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Executive Summary
                    </CardTitle>
                    <CardDescription>Platform overview and health status</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold">{platformDiscovery.platform_name}</div>
                          <div className="text-sm text-muted-foreground">Version {platformDiscovery.version}</div>
                        </div>
                        <Badge className="text-lg px-4 py-2">
                          {platformDiscovery.executive_summary.summary.health_status}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 border rounded-lg">
                          <div className="text-3xl font-bold text-blue-600">
                            {platformDiscovery.executive_summary.summary.total_modules}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">Backend Modules</div>
                        </div>
                        <div className="text-center p-4 border rounded-lg">
                          <div className="text-3xl font-bold text-purple-600">
                            {platformDiscovery.executive_summary.summary.total_api_endpoints}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">API Endpoints</div>
                        </div>
                        <div className="text-center p-4 border rounded-lg">
                          <div className="text-3xl font-bold text-green-600">
                            {platformDiscovery.executive_summary.summary.total_database_collections}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">DB Collections</div>
                        </div>
                        <div className="text-center p-4 border rounded-lg">
                          <div className="text-3xl font-bold text-orange-600">
                            {platformDiscovery.executive_summary.summary.total_frontend_pages}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">Frontend Pages</div>
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold mb-2">Key Capabilities</div>
                        <div className="flex flex-wrap gap-2">
                          {platformDiscovery.executive_summary.key_capabilities.map((cap, idx) => (
                            <Badge key={idx} variant="outline">{cap}</Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold mb-2">Architecture Highlights</div>
                        <div className="flex flex-wrap gap-2">
                          {platformDiscovery.executive_summary.architecture_highlights.map((highlight, idx) => (
                            <Badge key={idx} variant="secondary">{highlight}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Backend Modules */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileCode className="h-5 w-5 text-purple-600" />
                      Backend Modules ({platformDiscovery.modules.total_modules})
                    </CardTitle>
                    <CardDescription>Python services and modules</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {platformDiscovery.modules.key_services.map((service, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold">{service.name}</div>
                            <Badge variant={service.status === 'active' ? 'default' : 'secondary'}>
                              {service.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">{service.description}</div>
                          <div className="text-xs text-muted-foreground mt-1">File: {service.file}</div>
                        </div>
                      ))}
                      
                      <details className="p-3 border rounded-lg">
                        <summary className="font-semibold cursor-pointer">
                          All Modules ({platformDiscovery.modules.modules.length})
                        </summary>
                        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                          {platformDiscovery.modules.modules.map((module, idx) => (
                            <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                              <div className="font-medium">{module.name}.py</div>
                              <div className="text-xs text-muted-foreground">
                                {module.lines_of_code} lines • {module.size_kb} KB
                                {module.description && ` • ${module.description.substring(0, 80)}...`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </CardContent>
                </Card>

                {/* Database Architecture */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-green-600" />
                      Database Architecture
                    </CardTitle>
                    <CardDescription>
                      MongoDB collections and schemas ({platformDiscovery.database.total_collections} collections)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {platformDiscovery.database.key_collections.map((collection, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold">{collection.name}</div>
                            <Badge variant="outline">{collection.primary_key}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">{collection.purpose}</div>
                          <div className="text-xs">
                            <span className="text-muted-foreground">Relationships: </span>
                            {collection.relationships.map((rel, ridx) => (
                              <Badge key={ridx} variant="secondary" className="text-xs ml-1">
                                {rel}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}

                      <details className="p-3 border rounded-lg">
                        <summary className="font-semibold cursor-pointer">
                          All Collections ({platformDiscovery.database.collections.length})
                        </summary>
                        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                          {platformDiscovery.database.collections.map((coll, idx) => (
                            <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                              <div className="flex items-center justify-between">
                                <div className="font-medium">{coll.name}</div>
                                <Badge variant="outline" className="text-xs">
                                  {coll.document_count} docs
                                </Badge>
                              </div>
                              {coll.schema_fields.length > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Fields: {coll.schema_fields.map(f => f.field).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </CardContent>
                </Card>

                {/* API Endpoints */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Network className="h-5 w-5 text-blue-600" />
                      API Endpoints ({platformDiscovery.api_endpoints.total_endpoints})
                    </CardTitle>
                    <CardDescription>REST API endpoints by category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(platformDiscovery.api_endpoints.categories).map(([category, endpoints]) => (
                        <div key={category}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold">{category}</div>
                            <Badge>{endpoints.length} endpoints</Badge>
                          </div>
                          <div className="space-y-1 ml-4">
                            {endpoints.map((endpoint, idx) => (
                              <div key={idx} className="text-sm flex items-center gap-2 py-1">
                                <Badge 
                                  variant={
                                    endpoint.method === 'GET' ? 'outline' : 
                                    endpoint.method === 'POST' ? 'default' : 
                                    'secondary'
                                  }
                                  className="text-xs w-16 justify-center"
                                >
                                  {endpoint.method}
                                </Badge>
                                <code className="text-xs bg-slate-100 px-2 py-1 rounded flex-1">
                                  {endpoint.path}
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Agents */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-600" />
                      AI Agents
                    </CardTitle>
                    <CardDescription>
                      Core agents and custom agents ({platformDiscovery.agents.core_agents.length} core + {platformDiscovery.agents.custom_agents_count} custom)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {platformDiscovery.agents.core_agents.map((agent, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold">{agent.name}</div>
                            <Badge>{agent.type}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">{agent.description}</div>
                          <div className="text-xs">
                            <div className="text-muted-foreground mb-1">Capabilities:</div>
                            <div className="flex flex-wrap gap-1">
                              {agent.capabilities.map((cap, cidx) => (
                                <Badge key={cidx} variant="outline" className="text-xs">{cap}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Integrations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5 text-slate-600" />
                      External Integrations
                    </CardTitle>
                    <CardDescription>Connected external services and systems</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="font-semibold mb-2">LLM Providers</div>
                        <div className="space-y-2">
                          {platformDiscovery.integrations.llm_providers.map((provider, idx) => (
                            <div key={idx} className="p-2 border rounded-lg text-sm">
                              <div className="flex items-center justify-between">
                                <div className="font-medium">{provider.name}</div>
                                <Badge variant={provider.status === 'active' ? 'default' : 'outline'}>
                                  {provider.status}
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Models: {provider.models.join(', ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold mb-2">SAP Systems</div>
                        <div className="flex flex-wrap gap-2">
                          {platformDiscovery.integrations.sap_systems.map((system, idx) => (
                            <Badge key={idx} variant="secondary">
                              {system.name} {system.version}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Infrastructure */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-indigo-600" />
                      Infrastructure
                    </CardTitle>
                    <CardDescription>Runtime and deployment details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 border rounded-lg text-center">
                          <div className="font-semibold text-sm mb-1">Backend</div>
                          <div className="text-xs text-muted-foreground">{platformDiscovery.infrastructure.runtime.backend.framework}</div>
                          <Badge variant="outline" className="mt-2 text-xs">
                            Port {platformDiscovery.infrastructure.runtime.backend.port}
                          </Badge>
                        </div>
                        <div className="p-3 border rounded-lg text-center">
                          <div className="font-semibold text-sm mb-1">Frontend</div>
                          <div className="text-xs text-muted-foreground">{platformDiscovery.infrastructure.runtime.frontend.framework}</div>
                          <Badge variant="outline" className="mt-2 text-xs">
                            Port {platformDiscovery.infrastructure.runtime.frontend.port}
                          </Badge>
                        </div>
                        <div className="p-3 border rounded-lg text-center">
                          <div className="font-semibold text-sm mb-1">Database</div>
                          <div className="text-xs text-muted-foreground">{platformDiscovery.infrastructure.runtime.database.type}</div>
                          <Badge className="mt-2 text-xs">Running</Badge>
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold mb-2">Services Status</div>
                        <div className="space-y-1">
                          {platformDiscovery.infrastructure.services.map((service, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 border rounded text-sm">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="font-medium">{service.name}</span>
                                <Badge variant="outline" className="text-xs">{service.type}</Badge>
                              </div>
                              <Badge className="text-xs">{service.status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Discovery Results Tab */}
        <TabsContent value="discoveries">
          <div className="space-y-4">
            {discoveries.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No discovery results yet. Run discovery on tickets to see results here.
                </CardContent>
              </Card>
            ) : (
              discoveries.map((discovery) => (
                <Card key={discovery.id} data-testid={`discovery-${discovery.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{discovery.ticket.ticket_number}</Badge>
                          <Badge>{discovery.sap_system}</Badge>
                        </div>
                        <CardTitle className="text-xl">{discovery.ticket.title}</CardTitle>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Scanned</div>
                        <div className="text-sm font-medium">{new Date(discovery.scan_date).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 border rounded-lg text-center">
                          <div className="text-xl font-bold">{discovery.total_customizations}</div>
                          <div className="text-xs text-muted-foreground">Total</div>
                        </div>
                        <div className="p-3 border rounded-lg text-center">
                          <div className="text-xl font-bold text-green-600">{discovery.clean_core_compliant}</div>
                          <div className="text-xs text-muted-foreground">Compliant</div>
                        </div>
                        <div className="p-3 border rounded-lg text-center">
                          <div className="text-xl font-bold text-red-600">{discovery.non_compliant}</div>
                          <div className="text-xs text-muted-foreground">Non-Compliant</div>
                        </div>
                      </div>

                      <div>
                        <div className="font-medium mb-2">Findings</div>
                        <div className="space-y-2">
                          {discovery.findings.slice(0, 3).map((finding, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 border rounded text-sm">
                              {finding.clean_core_status === 'compliant' ? (
                                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                              ) : finding.clean_core_status === 'non_compliant' ? (
                                <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                              )}
                              <div className="flex-1">
                                <div className="font-medium">{finding.name}</div>
                                <div className="text-xs text-muted-foreground">{finding.type}</div>
                              </div>
                            </div>
                          ))}
                          {discovery.findings.length > 3 && (
                            <div className="text-xs text-muted-foreground text-center">
                              +{discovery.findings.length - 3} more findings
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
